import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_RD_PAYROLL_COST_FILE,
  AddRdPayrollCostFileMutation,
  AddRdPayrollCostFileMutationVariables,
  GET_BACKGROUND_OPERATIONS,
  GET_FISCAL_YEAR,
  GET_ONE_SCHEMA_JWT,
  GET_RD_PAYROLL_COST_SIGNED_UPLOAD_URL,
  GetBackgroundOperationsQueryVariables,
  GetFiscalYearQueryVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  GetRdPayrollCostSignedUploadUrlMutation,
  GetRdPayrollCostSignedUploadUrlMutationVariables,
  PayrollProvider,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';

type UseRdPayrollCostUploadParams = {
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
  payrollProvider: PayrollProvider;
};

type UseRdPayrollCostUploadReturn = {
  handleSuccess: () => Promise<void>;
  isLoading: boolean;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

export const useRdPayrollCostUpload = ({
  companyId,
  fiscalYearId,
  isOpen,
  payrollProvider,
}: UseRdPayrollCostUploadParams): UseRdPayrollCostUploadReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData, loading: isLoading }] = useMutation<
    GetRdPayrollCostSignedUploadUrlMutation,
    GetRdPayrollCostSignedUploadUrlMutationVariables
  >(GET_RD_PAYROLL_COST_SIGNED_UPLOAD_URL);
  const [addRdPayrollCostFile] = useMutation<AddRdPayrollCostFileMutation, AddRdPayrollCostFileMutationVariables>(
    ADD_RD_PAYROLL_COST_FILE,
  );

  // Fetch OneSchema JWT when modal opens
  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: {
            title: 'Error uploading R&D payroll cost file',
          },
        },
      });
    };

    initOneSchemaJwt();
  }, [companyId, getOneSchemaJwt, isOpen, user]);

  // Fetch signed upload URL when modal opens
  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen) {
        return;
      }
      // Generate filename based on provider: rippling-cost-{timestamp}.csv or rd-payroll-cost-{timestamp}.csv
      const providerPrefix =
        payrollProvider === PayrollProvider.Rippling
          ? 'rippling-cost'
          : payrollProvider === PayrollProvider.SelfReported
            ? 'rd-payroll-cost'
            : `${payrollProvider.toLowerCase()}-cost`;
      const fileName = `${providerPrefix}-${Date.now()}.csv`;
      await getUploadUrlMutation({
        variables: { companyId, fileName },
        context: {
          errorToast: {
            title: 'Error uploading R&D payroll cost file',
          },
        },
      });
    };

    initUploadUrls();
  }, [companyId, getUploadUrlMutation, isOpen, payrollProvider]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getRdPayrollCostSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }
    await addRdPayrollCostFile({
      variables: {
        fiscalYearId,
        addRdPayrollCostFileInput: {
          permanentUrl,
          payrollProvider,
        },
      },
      refetchQueries: [
        {
          query: GET_FISCAL_YEAR,
          variables: { fiscalYearId } satisfies GetFiscalYearQueryVariables,
        },
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'R&D payroll cost file import started!',
          description:
            "We're processing your payroll cost file. This usually takes a few minutes. When it's done, the data will load automatically on this page.",
        },
      },
    });
  }, [addRdPayrollCostFile, companyId, fiscalYearId, payrollProvider, uploadUrlData]);

  return {
    handleSuccess,
    isLoading,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getRdPayrollCostSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getRdPayrollCostSignedUploadUrl.signedUrl ?? null,
  };
};
